import Splide, { Options } from "@splidejs/splide";
import { useModal } from '@features/Shared/Modal/_js/modal';

export const useGalleryCarousel = () => {
  const {showModal, setModalContent, closeModal} = useModal();
  let splideCarousels : { [key: string]: Splide } = {};

  // Carousel options
  const galleryCarouselOptions: Options = {
    type: 'loop',
    speed: 500,
    width: '100%',
    start: 0,
    focus: 0,
    perMove: 1,
    easing: 'ease',
    drag: true,
    flickPower: 150,
    flickMaxPages: 1,
    keyboard: true,
    updateOnMove: true,
    arrows: false,
    rewind: false
  };

  const setUpCarousel = (carouselSection: HTMLElement): void => {
    const carousel = new Splide(carouselSection, galleryCarouselOptions);

    carousel.on('move', () => {
      const isFirstSlide = carousel.index === 0;
      const prevArrow = carouselSection.querySelector('.gallery-block__arrow--prev') as HTMLElement;

      if (prevArrow){
        prevArrow.classList.toggle('gallery-block__arrow--hidden', isFirstSlide);
      }
    });

    carousel.mount();
    splideCarousels[carouselSection.id] = carousel;

    // Get navigation arrow elements
    const prevArrow = carouselSection.querySelector('button.gallery-block__arrow--prev') as HTMLElement;
    const nextArrow = carouselSection.querySelector('button.gallery-block__arrow--next') as HTMLElement;
    const isFirstSlide = carousel.index === 0;

    if (prevArrow) {
      prevArrow.classList.toggle('gallery-block__arrow--hidden', isFirstSlide);
    }

    // Store event handler functions for later removal
    const prevHandler = () => goToPrevSlide(carousel);
    const nextHandler = () => goToNextSlide(carousel);

    prevArrow.addEventListener('click', prevHandler);
    nextArrow.addEventListener('click', nextHandler);

    // Attach the function references to the element for easy removal later.
    (prevArrow as any)._prevHandler = prevHandler;
    (nextArrow as any)._nextHandler = nextHandler;

    // Open fullscreen modal
    const modalToggle = carouselSection.querySelector(".gallery-block__modal-toggle");
    
    if (modalToggle) {
      const initModalHandler = () => initializeModal(carouselSection);
      modalToggle.addEventListener('click', initModalHandler);
      (modalToggle as any)._initModalHandler = initModalHandler;
    }

    // Close modal event listener
    const modal = document.querySelector(".gallery-block__modal");
    if (modal) {
      modal.addEventListener("closeModal", closeGalleryModal);
    }
  };
  
  function goToPrevSlide(carousel: Splide) {
    return carousel.go('<');
  }

  function goToNextSlide(carousel: Splide) {
    return carousel.go('>');
  }

  function setSplideIndex(oldCarouselSection: HTMLElement, newCarouselSection: HTMLElement): void {
    // Get the current index of the original carousel
    const splideCarousel = splideCarousels[oldCarouselSection.id];
    const splideCarouselCurrentIndex = splideCarousel.Components.Controller.getIndex();

    // Set the current index of the modal carousel to the same as the original carousel
    const modalSplideCarousel = splideCarousels[newCarouselSection.id];
    modalSplideCarousel.go(splideCarouselCurrentIndex);
  }

  const initializeModal = (carouselSection: HTMLElement): void => {
    showModal("gallery-block__modal").then(() => {
      // Before opening modal, destroy current event listeners and carousel instance.
      destroyGalleryCarousel(carouselSection);
      
      // Set up the modal and the carousel inside
      const carouselSectionCopy = carouselSection.cloneNode(true) as HTMLElement;
      carouselSection.classList.add("gallery-block__carousel--hidden");
      carouselSectionCopy.id  = `${carouselSection.id}-modal`;
      carouselSectionCopy.classList.remove("gallery-block__carousel--hidden");
      
      setModalContent(carouselSectionCopy.outerHTML);

      // Add media query to sizes attribute to all images in the carousel
      document.querySelector(".gallery-block__modal")?.querySelectorAll("img").forEach(image => {
        image.sizes = "100vw";
      })
      
      document.querySelector(".gallery-block__modal .modal__content")?.classList.add("gallery-block");
      setUpCarousel(document.querySelector(".gallery-block__modal .gallery-block__carousel") as HTMLElement);
      
      // Set splide index to the current index of the original carousel
      setSplideIndex(carouselSection, carouselSectionCopy);
    });
  };

  const closeGalleryModal = (e: Event) => {
    // Destroy the modal carousel
    const modalCarouselSection = document.querySelector(".gallery-block__modal .gallery-block__carousel") as HTMLElement;
    destroyGalleryCarousel(modalCarouselSection);
    
    // Set up the original carousel
    const id = (e.target as HTMLElement).querySelector(".gallery-block__carousel")?.id.split("-modal")[0];
    const currentOpenCarouselSection = document.getElementById(id as string) as HTMLElement;
    currentOpenCarouselSection.classList.remove("gallery-block__carousel--hidden");
    setUpCarousel(currentOpenCarouselSection);

    // Set splide index to the current index of the modal carousel
    setSplideIndex(modalCarouselSection, currentOpenCarouselSection);
  };
  
  const destroyGalleryCarousel = (carouselSection: HTMLElement): void => {
    // Destroy the Splide instance.
    const carousel = splideCarousels[carouselSection.id];
    carousel.destroy(true);
    carouselSection.classList.remove("is-initialized");
    
    // Remove event listener for modal toggle
    const modalToggle = carouselSection.querySelector(".gallery-block__modal-toggle");
    if (modalToggle && (modalToggle as any)._initModalHandler) {
      modalToggle.removeEventListener("click", (modalToggle as any)._initModalHandler);
    }

    // Remove close modal listener
    const modal = document.querySelector(".gallery-block__modal");
    if (modal) {
      modal.removeEventListener("closeModal", closeGalleryModal);
    }

    // Remove navigation event listeners
    const prevArrow = carouselSection.querySelector('button.gallery-block__arrow--prev') as HTMLElement;
    const nextArrow = carouselSection.querySelector('button.gallery-block__arrow--next') as HTMLElement;
    
    if (prevArrow && (prevArrow as any)._prevHandler) {
      prevArrow.removeEventListener('click', (prevArrow as any)._prevHandler);
    }
    
    if (nextArrow && (nextArrow as any)._nextHandler) {
      nextArrow.removeEventListener('click', (nextArrow as any)._nextHandler);
    }
  };

  // Initialize all splideCarousels
  document.querySelectorAll('.gallery-block__carousel').forEach((carousel) => {
    setUpCarousel(carousel as HTMLElement);
  });
};

// Ensure the script runs once the DOM is ready.
document.addEventListener('DOMContentLoaded', useGalleryCarousel);
